import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Handshake, Check, X, MessageSquare, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import axios from 'axios';
import { toast } from 'sonner';
import { formatJpWithAlias } from '@/utils/partDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerPriceRequestsButton = () => {
  const { token, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [pendingCounterOffers, setPendingCounterOffers] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [canRequest, setCanRequest] = useState(false);
  const pollIntervalRef = useRef(null);

  const fetchRequests = useCallback(async () => {
    if (!token) return;
    
    try {
      // Check eligibility
      const eligibilityRes = await axios.get(`${API}/price-requests/check-eligibility`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCanRequest(eligibilityRes.data.can_request_special_price);
      
      if (!eligibilityRes.data.can_request_special_price) {
        return;
      }
      
      // Fetch requests
      const response = await axios.get(`${API}/price-requests/my-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allRequests = response.data.requests || [];
      setRequests(allRequests);
      
      // Count pending counter offers
      const counterOffers = allRequests.filter(r => r.status === 'counter_offered');
      setPendingCounterOffers(counterOffers.length);
    } catch (error) {
      console.error('Error fetching price requests:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchRequests();
    
    // Poll every 30 seconds
    pollIntervalRef.current = setInterval(fetchRequests, 30000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchRequests]);

  const handleAcceptCounter = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.post(`${API}/price-requests/${requestId}/accept-counter`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Counter offer accepted! Special price is now active.');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept counter offer');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectCounter = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.post(`${API}/price-requests/${requestId}/reject-counter`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.info('Counter offer rejected');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject counter offer');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700 text-xs">Pending</Badge>;
      case 'accepted':
        return <Badge className="bg-green-100 text-green-700 text-xs">Accepted</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700 text-xs">Rejected</Badge>;
      case 'counter_offered':
        return <Badge className="bg-blue-100 text-blue-700 text-xs animate-pulse">Counter Offer</Badge>;
      case 'counter_accepted':
        return <Badge className="bg-green-100 text-green-700 text-xs">Price Set</Badge>;
      case 'counter_rejected':
        return <Badge className="bg-gray-100 text-gray-700 text-xs">Declined</Badge>;
      default:
        return null;
    }
  };

  // Don't render if user is not eligible
  if (!canRequest) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative h-9 w-9 p-0 hover:bg-amber-50"
          title="Price Requests"
        >
          <Handshake className="w-5 h-5 text-amber-600" />
          {pendingCounterOffers > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center bg-red-500 text-white text-xs animate-bounce"
            >
              {pendingCounterOffers}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 bg-white border-2 shadow-xl" align="end">
        <div className="p-3 border-b bg-amber-50">
          <h3 className="font-semibold text-amber-800 flex items-center gap-2">
            <Handshake className="w-4 h-4" />
            My Price Requests
          </h3>
        </div>
        
        <div className="max-h-80 overflow-y-auto">
          {requests.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              No price requests yet
            </div>
          ) : (
            requests.map((request) => (
              <div 
                key={request.id} 
                className={`p-3 border-b hover:bg-gray-50 ${request.status === 'counter_offered' ? 'bg-blue-50' : ''}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-mono text-xs text-blue-600">{formatJpWithAlias(request)}</p>
                    <p className="text-xs text-gray-600 line-clamp-1">{request.part_name}</p>
                  </div>
                  {getStatusBadge(request.status)}
                </div>
                
                <div className="mt-2 text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Your Price:</span>
                    <span className="font-semibold">₹{request.requested_price?.toLocaleString('en-IN')}</span>
                  </div>
                  
                  {request.status === 'counter_offered' && (
                    <>
                      <div className="flex justify-between text-blue-700 font-semibold">
                        <span>Counter Offer:</span>
                        <span>₹{request.counter_offer_price?.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700"
                          onClick={() => handleAcceptCounter(request.id)}
                          disabled={processing === request.id}
                        >
                          {processing === request.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <Check className="w-3 h-3 mr-1" />
                              Accept
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => handleRejectCounter(request.id)}
                          disabled={processing === request.id}
                        >
                          <X className="w-3 h-3 mr-1" />
                          Decline
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CustomerPriceRequestsButton;
